import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Inbox as InboxIcon, Mail, MailOpen, Trash2, Bell, MessageCircle, DollarSign, FileText, Gift, Heart, Image as ImageIcon, Video, Mic, FolderHeart, Download, ChevronDown, ShieldCheck, Flower2, Landmark } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const mediaMeta = {
  image: { Icon: ImageIcon, label: 'Photo' },
  video: { Icon: Video, label: 'Video' },
  audio: { Icon: Mic, label: 'Voice' },
  voice: { Icon: Mic, label: 'Voice' },
};

const MediaTag = ({ type, dense }) => {
  const m = mediaMeta[type];
  if (!m) return null;
  const { Icon, label } = m;
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full bg-[#EBF0F5] text-[#2B4C7E] px-2 py-0.5 text-[11px] font-medium"
      data-testid={`media-tag-${type}`}
      title={`Includes a ${label.toLowerCase()}`}
    >
      <Icon className="h-3 w-3" />
      {!dense && label}
    </span>
  );
};

const PkgSection = ({ id, title, icon: Icon, openSection, setOpenSection, testid, children, checkable, checked, onToggleCheck }) => {
  const open = openSection === id;
  return (
    <div className={`border rounded-xl overflow-hidden transition-colors ${checked ? 'border-green-200 bg-green-50/40' : 'border-gray-100'}`} data-testid={testid}>
      <div className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors">
        <button onClick={() => setOpenSection(open ? null : id)} className="flex items-center gap-2 font-medium text-gray-800 flex-1 text-left" data-testid={`${testid}-toggle`}>
          <Icon className="h-4 w-4 text-[#2B4C7E]" /> {title}
          {checked && <span className="text-xs text-green-600 font-semibold ml-1">✓ Handled</span>}
        </button>
        <div className="flex items-center gap-3">
          {checkable && (
            <label className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer" onClick={(e) => e.stopPropagation()} data-testid={`${testid}-check-label`}>
              <input
                type="checkbox"
                checked={!!checked}
                onChange={(e) => onToggleCheck(id, e.target.checked)}
                className="h-4 w-4 rounded accent-[#2B4C7E] cursor-pointer"
                data-testid={`${testid}-check`}
              />
              Mark handled
            </label>
          )}
          <button onClick={() => setOpenSection(open ? null : id)}>
            <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
          </button>
        </div>
      </div>
      {open && <div className="p-4 text-sm text-gray-700 whitespace-pre-wrap" data-testid={`${testid}-content`}>{children}</div>}
    </div>
  );
};

const prettyKey = (k) => k.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
const KV = ({ data }) => (
  <div className="space-y-1">
    {Object.entries(data || {}).filter(([, v]) => v !== null && v !== '' && v !== undefined).map(([k, v]) => (
      <div key={k}><span className="font-medium text-gray-600">{prettyKey(k)}:</span> {String(v)}</div>
    ))}
  </div>
);


const notificationIcons = {
  whisper: MessageCircle,
  distribution: DollarSign,
  release_package: Gift,
  passing_package: Heart,
  will: FileText,
  general: Bell
};

const notificationColors = {
  whisper: 'bg-[#EBF0F5] text-[#2B4C7E]',
  distribution: 'bg-green-100 text-green-600',
  release_package: 'bg-[#EBF0F5] text-[#2B4C7E]',
  passing_package: 'bg-[#F2E9D9] text-[#B3956B]',
  will: 'bg-[#F2E9D9] text-[#B3956B]',
  general: 'bg-gray-100 text-gray-600'
};

export default function Inbox() {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [filter, setFilter] = useState('all'); // all, unread, whispers, packages
  const [packageDetail, setPackageDetail] = useState(null);
  const [loadingPackage, setLoadingPackage] = useState(false);
  const [openSection, setOpenSection] = useState(null);
  const [savingPackage, setSavingPackage] = useState(false);

  useEffect(() => {
    loadNotifications();
  }, []);

  // When a release/passing package is opened, fetch its full contents so the
  // beneficiary can actually open and save the information they received.
  useEffect(() => {
    setPackageDetail(null);
    setOpenSection(null);
    const n = selectedNotification;
    const deceasedId = n?.package_data?.deceased_user_id;
    if (!n || (n.type !== 'release_package' && n.type !== 'passing_package') || !deceasedId) return;
    const token = localStorage.getItem('session_token');
    setLoadingPackage(true);
    axios.get(`${BACKEND_URL}/api/easy-passing/package/${deceasedId}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => setPackageDetail(r.data))
      .catch((e) => toast.error(e.response?.data?.detail || 'Could not open this package.'))
      .finally(() => setLoadingPackage(false));
  }, [selectedNotification]);

  const savePackage = async () => {
    const deceasedId = selectedNotification?.package_data?.deceased_user_id;
    if (!deceasedId) return;
    const token = localStorage.getItem('session_token');
    setSavingPackage(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/easy-passing/package/${deceasedId}/download`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(packageDetail?.deceased_name || 'loved-one').replace(/[^A-Za-z0-9]+/g, '-')}-EasyPassing-Package.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Package saved to your device 💜');
    } catch (e) {
      toast.error('Could not download the package. Please try again.');
    } finally {
      setSavingPackage(false);
    }
  };

  const downloadWillDocument = () => {
    const url = packageDetail?.will?.document_url;
    if (!url) return;
    const a = document.createElement('a');
    a.href = url;
    a.download = packageDetail?.will?.document_name || 'Last-Will.pdf';
    a.target = '_blank';
    a.click();
  };

  const toggleChecklist = async (item, done) => {
    const deceasedId = selectedNotification?.package_data?.deceased_user_id;
    const token = localStorage.getItem('session_token');
    setPackageDetail((p) => ({ ...p, checklist: { ...(p?.checklist || {}), [item]: { done } } }));
    try {
      await axios.put(`${BACKEND_URL}/api/easy-passing/package/${deceasedId}/checklist`,
        { item, done }, { headers: { Authorization: `Bearer ${token}` } });
    } catch (e) {
      toast.error('Could not update the checklist.');
    }
  };

  const toggleDistribution = async (distId, released) => {
    const deceasedId = selectedNotification?.package_data?.deceased_user_id;
    const token = localStorage.getItem('session_token');
    setPackageDetail((p) => ({
      ...p,
      all_distributions: (p?.all_distributions || []).map((d) =>
        d.distribution_id === distId ? { ...d, status: released ? 'released' : 'pending' } : d),
    }));
    try {
      await axios.put(`${BACKEND_URL}/api/easy-passing/package/${deceasedId}/distribution/${distId}`,
        { released }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(released ? 'Marked as released/paid 💜' : 'Marked as pending');
    } catch (e) {
      toast.error('Could not update this distribution.');
    }
  };

  const isChecked = (id) => !!packageDetail?.checklist?.[id]?.done;

  const loadNotifications = async () => {
    const token = localStorage.getItem('session_token');
    if (!token) {
      navigate('/');
      return;
    }

    try {
      const response = await axios.get(`${BACKEND_URL}/api/inbox`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(response.data);
    } catch (error) {
      console.error('Failed to load inbox:', error);
      toast.error('Failed to load inbox');
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (notificationId) => {
    const token = localStorage.getItem('session_token');
    try {
      await axios.put(
        `${BACKEND_URL}/api/inbox/${notificationId}/read`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNotifications(notifications.map(n => 
        n.notification_id === notificationId ? { ...n, is_read: true } : n
      ));
    } catch (error) {
      console.error('Failed to mark as read:', error);
    }
  };

  const deleteNotification = async (notificationId) => {
    const token = localStorage.getItem('session_token');
    try {
      await axios.delete(`${BACKEND_URL}/api/inbox/${notificationId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(notifications.filter(n => n.notification_id !== notificationId));
      toast.success('Notification deleted');
      if (selectedNotification?.notification_id === notificationId) {
        setSelectedNotification(null);
      }
    } catch (error) {
      console.error('Failed to delete notification:', error);
      toast.error('Failed to delete notification');
    }
  };

  const filteredNotifications = notifications.filter(n => {
    if (filter === 'all') return true;
    if (filter === 'unread') return !n.is_read;
    if (filter === 'whispers') return n.type === 'whisper';
    if (filter === 'packages') return n.type === 'release_package' || n.type === 'passing_package';
    return true;
  });

  const unreadCount = notifications.filter(n => !n.is_read).length;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#EBF0F5] via-[#EBF0F5] to-[#EBF0F5]">
        <div className="text-center">
          <div className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-[#2B4C7E] border-r-transparent mb-4"></div>
          <p className="text-gray-600">Loading inbox...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#EBF0F5] via-[#EBF0F5] to-[#EBF0F5]">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-[#EBF0F5] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-center gap-2 sm:gap-4 min-w-0">
              <Link to="/dashboard">
                <Button variant="ghost" size="sm" className="rounded-full flex-shrink-0">
                  <ArrowLeft className="h-5 w-5" />
                </Button>
              </Link>
              <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                <div className="relative flex-shrink-0">
                  <InboxIcon className="h-5 w-5 sm:h-6 sm:w-6 text-[#2B4C7E]" />
                  {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                      {unreadCount}
                    </span>
                  )}
                </div>
                <h1 className="text-lg sm:text-2xl font-medium bg-gradient-to-r from-[#2B4C7E] to-[#2B4C7E] bg-clip-text text-transparent truncate">
                  Inbox
                </h1>
              </div>
            </div>
            
            {/* Filter Tabs */}
            <div className="flex gap-1 sm:gap-2 overflow-x-auto pb-1 sm:pb-0">
              {[
                { key: 'all', label: 'All' },
                { key: 'unread', label: 'Unread' },
                { key: 'whispers', label: 'Whispers' },
                { key: 'packages', label: 'Packages' }
              ].map(tab => (
                <Button
                  key={tab.key}
                  variant={filter === tab.key ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setFilter(tab.key)}
                  className={`rounded-full text-xs sm:text-sm px-2 sm:px-3 flex-shrink-0 ${filter === tab.key ? 'bg-[#2B4C7E] text-white' : ''}`}
                >
                  {tab.label}
                </Button>
              ))}
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto p-4 md:p-8">
        {filteredNotifications.length === 0 ? (
          <div className="text-center py-24">
            <div className="w-20 h-20 bg-[#EBF0F5] rounded-full flex items-center justify-center mx-auto mb-6">
              <InboxIcon className="h-10 w-10 text-[#3A5C8F]" />
            </div>
            <h3 className="text-2xl font-medium text-gray-800 mb-3">
              {filter === 'all' ? 'Your inbox is empty' : `No ${filter} notifications`}
            </h3>
            <p className="text-gray-600">
              Messages and whispers from your beneficiaries will appear here
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Notification List */}
            <div className="lg:col-span-1 space-y-3">
              <AnimatePresence>
                {filteredNotifications.map((notification, index) => {
                  const Icon = notificationIcons[notification.type] || Bell;
                  const colorClass = notificationColors[notification.type] || notificationColors.general;
                  const isSelected = selectedNotification?.notification_id === notification.notification_id;
                  
                  return (
                    <motion.div
                      key={notification.notification_id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, x: -100 }}
                      transition={{ delay: index * 0.05 }}
                    >
                      <Card
                        className={`p-4 cursor-pointer transition-all ${
                          isSelected 
                            ? 'border-2 border-[#2B4C7E] shadow-lg' 
                            : notification.is_read 
                              ? 'border border-gray-200 hover:border-[#B9C7D6]' 
                              : 'border-2 border-[#D7E0EA] bg-[#EBF0F5]/50'
                        }`}
                        onClick={() => {
                          setSelectedNotification(notification);
                          if (!notification.is_read) {
                            markAsRead(notification.notification_id);
                          }
                        }}
                      >
                        <div className="flex items-start gap-3">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${colorClass}`}>
                            <Icon className="h-5 w-5" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between mb-1">
                              <h4 className={`font-semibold truncate ${notification.is_read ? 'text-gray-700' : 'text-gray-900'}`}>
                                {notification.title}
                              </h4>
                              {!notification.is_read && (
                                <span className="w-2 h-2 bg-[#2B4C7E] rounded-full flex-shrink-0"></span>
                              )}
                            </div>
                            <p className="text-sm text-gray-500 truncate">{notification.preview}</p>
                            <div className="flex items-center gap-2 mt-1">
                              <p className="text-xs text-gray-400">
                                {new Date(notification.created_at).toLocaleDateString()}
                              </p>
                              {notification.media_url && <MediaTag type={notification.media_type} dense />}
                              {notification.album && (
                                <span className="inline-flex items-center gap-1 rounded-full bg-[#EBF0F5] text-[#2B4C7E] px-2 py-0.5 text-[11px] font-medium" data-testid="media-tag-album" title="Includes a photo album">
                                  <FolderHeart className="h-3 w-3" />
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </Card>
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            </div>

            {/* Notification Detail */}
            <div className="lg:col-span-2">
              {selectedNotification ? (
                <Card className="p-6 rounded-2xl">
                  <div className="flex items-start justify-between mb-6">
                    <div className="flex items-center gap-4">
                      <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${notificationColors[selectedNotification.type] || notificationColors.general}`}>
                        {(() => {
                          const Icon = notificationIcons[selectedNotification.type] || Bell;
                          return <Icon className="h-7 w-7" />;
                        })()}
                      </div>
                      <div>
                        <h2 className="text-xl font-bold text-gray-900">{selectedNotification.title}</h2>
                        <p className="text-sm text-gray-500">
                          From: {selectedNotification.sender_name || 'System'} • {new Date(selectedNotification.created_at).toLocaleString()}
                        </p>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => deleteNotification(selectedNotification.notification_id)}
                      className="text-red-500 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="h-5 w-5" />
                    </Button>
                  </div>

                  <div className="prose prose-purple max-w-none">
                    {selectedNotification.crest_url && (
                      <div className="flex justify-center mb-4" data-testid="inbox-crest">
                        <img
                          src={`${BACKEND_URL}${selectedNotification.crest_url}`}
                          alt="Family crest"
                          className="h-32 w-auto"
                        />
                      </div>
                    )}
                    <div className="bg-gray-50 rounded-xl p-6 whitespace-pre-wrap">
                      {selectedNotification.content}
                    </div>
                  </div>

                  {/* Attached media (photo / video / voice) */}
                  {selectedNotification.media_url && (
                    <div className="mt-6" data-testid="inbox-media">
                      {selectedNotification.media_type === 'image' && (
                        <img
                          src={selectedNotification.media_url}
                          alt="Attached to this whisper"
                          className="w-full max-h-[420px] object-contain rounded-xl border border-gray-100 bg-black/5"
                          data-testid="inbox-media-image"
                        />
                      )}
                      {selectedNotification.media_type === 'video' && (
                        <video
                          src={selectedNotification.media_url}
                          controls
                          className="w-full max-h-[420px] rounded-xl bg-black"
                          data-testid="inbox-media-video"
                        />
                      )}
                      {(selectedNotification.media_type === 'audio' || selectedNotification.media_type === 'voice') && (
                        <audio
                          src={selectedNotification.media_url}
                          controls
                          className="w-full"
                          data-testid="inbox-media-audio"
                        />
                      )}
                    </div>
                  )}

                  {/* Attached photo album */}
                  {selectedNotification.album && (
                    <div className="mt-6" data-testid="inbox-album">
                      <div className="flex items-center gap-2 mb-3">
                        <FolderHeart className="h-5 w-5 text-[#2B4C7E]" />
                        <h4 className="font-semibold text-gray-800">{selectedNotification.album.title || 'Photo Album'}</h4>
                      </div>
                      {(selectedNotification.album.photos || []).length > 0 && (
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-3">
                          {selectedNotification.album.photos.map((src, i) => (
                            <img key={`ph${i}`} src={src} alt="Album" className="w-full h-32 object-cover rounded-xl border border-gray-100" data-testid={`inbox-album-photo-${i}`} />
                          ))}
                        </div>
                      )}
                      {(selectedNotification.album.videos || []).length > 0 && (
                        <div className="space-y-2 mb-3">
                          {selectedNotification.album.videos.map((src, i) => (
                            <video key={`vd${i}`} src={src} controls className="w-full max-h-[360px] rounded-xl bg-black" data-testid={`inbox-album-video-${i}`} />
                          ))}
                        </div>
                      )}
                      {(selectedNotification.album.audio_recordings || []).length > 0 && (
                        <div className="space-y-2">
                          {selectedNotification.album.audio_recordings.map((src, i) => (
                            <audio key={`au${i}`} src={src} controls className="w-full" data-testid={`inbox-album-audio-${i}`} />
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Release / Passing Package — open & save received information */}
                  {(selectedNotification.type === 'passing_package' || selectedNotification.type === 'release_package') && (
                    <div className="mt-6" data-testid="package-viewer">
                      <div className="flex items-center justify-between mb-3 gap-3">
                        <h4 className="font-semibold text-gray-800">Released Information</h4>
                        <Button
                          onClick={savePackage}
                          disabled={savingPackage || !packageDetail}
                          className="rounded-full bg-gradient-to-r from-[#2B4C7E] to-[#C8A97E] text-white hover:from-[#1E3A63] hover:to-[#B3956B]"
                          data-testid="save-package-btn"
                        >
                          <Download className="h-4 w-4 mr-2" />
                          {savingPackage ? 'Preparing…' : 'Save package (PDF)'}
                        </Button>
                      </div>

                      {loadingPackage && (
                        <p className="text-sm text-gray-500" data-testid="package-loading">Opening the package…</p>
                      )}

                      {packageDetail && (() => {
                        const primary = packageDetail.is_primary;
                        const presentSections = [
                          packageDetail.obituary && 'obituary',
                          packageDetail.will && 'will',
                          packageDetail.belongings && 'belongings',
                          packageDetail.funeral_preferences && 'funeral',
                          ((primary ? packageDetail.all_distributions : packageDetail.distributions) || []).length && 'distributions',
                          (packageDetail.life_insurance || []).length && 'life_insurance',
                          (packageDetail.home_insurance || []).length && 'home_insurance',
                          (packageDetail.car_insurance || []).length && 'car_insurance',
                          (packageDetail.credentials || []).length && 'credentials',
                        ].filter(Boolean);
                        const doneCount = presentSections.filter((s) => isChecked(s)).length;
                        const pct = presentSections.length ? Math.round((doneCount / presentSections.length) * 100) : 0;
                        return (
                        <div className="space-y-2" data-testid="package-sections">
                          {primary && presentSections.length > 0 && (
                            <div className="mb-4 p-3 rounded-xl bg-[#F7F1E8]/60 border border-[#EAD9BF]" data-testid="executor-progress">
                              <div className="flex justify-between text-sm mb-1.5">
                                <span className="font-medium text-[#836A45]">Executor checklist</span>
                                <span className="text-[#836A45]" data-testid="executor-progress-count">{doneCount} of {presentSections.length} handled</span>
                              </div>
                              <div className="h-2 rounded-full bg-white overflow-hidden">
                                <div className="h-full bg-gradient-to-r from-[#2B4C7E] to-[#C8A97E] transition-all" style={{ width: `${pct}%` }} />
                              </div>
                            </div>
                          )}

                          {packageDetail.obituary && (
                            <PkgSection id="obituary" title="Obituary" icon={Heart} openSection={openSection} setOpenSection={setOpenSection} testid="pkg-obituary" checkable={primary} checked={isChecked('obituary')} onToggleCheck={toggleChecklist}>
                              {packageDetail.obituary.photo_url && (
                                <img src={packageDetail.obituary.photo_url} alt="In memory" className="w-40 h-40 object-cover rounded-xl mb-3" />
                              )}
                              {packageDetail.obituary.content}
                            </PkgSection>
                          )}

                          {packageDetail.will && (
                            <PkgSection id="will" title="Last Will & Testament" icon={FileText} openSection={openSection} setOpenSection={setOpenSection} testid="pkg-will" checkable={primary} checked={isChecked('will')} onToggleCheck={toggleChecklist}>
                              {packageDetail.will.location && (
                                <p className="mb-3"><span className="font-medium text-gray-600">Physical location:</span> {packageDetail.will.location}</p>
                              )}
                              {packageDetail.will.document_url ? (
                                <Button onClick={downloadWillDocument} variant="outline" className="rounded-full" data-testid="pkg-will-download">
                                  <Download className="h-4 w-4 mr-2" /> Open {packageDetail.will.document_name || 'Will document'}
                                </Button>
                              ) : (
                                <p className="text-gray-500">No uploaded document — see physical location above.</p>
                              )}
                            </PkgSection>
                          )}

                          {packageDetail.belongings && (
                            <PkgSection id="belongings" title="Belongings Instructions" icon={Gift} openSection={openSection} setOpenSection={setOpenSection} testid="pkg-belongings" checkable={primary} checked={isChecked('belongings')} onToggleCheck={toggleChecklist}>
                              {packageDetail.belongings.instructions}
                            </PkgSection>
                          )}

                          {packageDetail.funeral_preferences && (
                            <PkgSection id="funeral" title="Funeral Preferences" icon={Flower2} openSection={openSection} setOpenSection={setOpenSection} testid="pkg-funeral" checkable={primary} checked={isChecked('funeral')} onToggleCheck={toggleChecklist}>
                              <KV data={packageDetail.funeral_preferences} />
                            </PkgSection>
                          )}

                          {((primary ? packageDetail.all_distributions : packageDetail.distributions) || []).length > 0 && (
                            <PkgSection id="distributions" title={primary ? 'Distributions (all beneficiaries)' : `Your Distributions ($${(packageDetail.distribution_total || 0).toFixed(2)})`} icon={DollarSign} openSection={openSection} setOpenSection={setOpenSection} testid="pkg-distributions" checkable={primary} checked={isChecked('distributions')} onToggleCheck={toggleChecklist}>
                              <div className="space-y-2">
                                {(primary ? packageDetail.all_distributions : packageDetail.distributions).map((d, i) => {
                                  const released = d.status === 'released' || d.status === 'completed';
                                  return (
                                    <div key={d.distribution_id || i} className="flex items-center justify-between border-b border-gray-100 pb-2 last:border-0 gap-3" data-testid={`distribution-row-${d.distribution_id || i}`}>
                                      <div className="min-w-0">
                                        <div className="truncate">{primary && d.beneficiary_name ? <span className="font-medium">{d.beneficiary_name}</span> : (d.notes || d.account_type || 'Distribution')}</div>
                                        {primary && (d.notes || d.account_type) && <div className="text-xs text-gray-500 truncate">{d.notes || d.account_type}</div>}
                                      </div>
                                      <div className="flex items-center gap-3 flex-shrink-0">
                                        <span className="font-semibold text-green-600">${(d.amount || 0).toFixed(2)}</span>
                                        {primary ? (
                                          <Button
                                            size="sm"
                                            variant={released ? 'default' : 'outline'}
                                            onClick={() => toggleDistribution(d.distribution_id, !released)}
                                            className={`rounded-full text-xs ${released ? 'bg-green-600 hover:bg-green-700 text-white' : ''}`}
                                            data-testid={`distribution-toggle-${d.distribution_id || i}`}
                                          >
                                            {released ? '✓ Paid' : 'Mark paid'}
                                          </Button>
                                        ) : (
                                          <span className={`text-xs px-2 py-0.5 rounded-full ${released ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                                            {released ? 'Released' : 'Pending'}
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </PkgSection>
                          )}

                          {['life_insurance', 'home_insurance', 'car_insurance'].map((coll) => (
                            (packageDetail[coll] && packageDetail[coll].length > 0) ? (
                              <PkgSection key={coll} id={coll} title={prettyKey(coll)} icon={Landmark} openSection={openSection} setOpenSection={setOpenSection} testid={`pkg-${coll}`} checkable={primary} checked={isChecked(coll)} onToggleCheck={toggleChecklist}>
                                <div className="space-y-4">
                                  {packageDetail[coll].map((it, i) => (<div key={i} className="border-b border-gray-100 pb-3 last:border-0"><KV data={it} /></div>))}
                                </div>
                              </PkgSection>
                            ) : null
                          ))}

                          {packageDetail.credentials && packageDetail.credentials.length > 0 && (
                            <PkgSection id="credentials" title="Credentials Vault (Primary Executor)" icon={ShieldCheck} openSection={openSection} setOpenSection={setOpenSection} testid="pkg-credentials" checkable={primary} checked={isChecked('credentials')} onToggleCheck={toggleChecklist}>
                              <div className="space-y-3">
                                {packageDetail.credentials.map((c, i) => (
                                  <div key={i} className="border-b border-gray-100 pb-2 last:border-0">
                                    <div className="font-medium text-gray-800">{c.platform}</div>
                                    {c.username && <div><span className="text-gray-600">Username:</span> {c.username}</div>}
                                    {c.password && <div><span className="text-gray-600">Password:</span> <code className="bg-gray-100 px-1 rounded">{c.password}</code></div>}
                                    {c.notes && <div className="text-gray-500">{c.notes}</div>}
                                  </div>
                                ))}
                              </div>
                            </PkgSection>
                          )}

                          {presentSections.length === 0 && (
                            <p className="text-sm text-gray-500" data-testid="package-empty">No additional documents were stored for release.</p>
                          )}
                        </div>
                        );
                      })()}
                    </div>
                  )}
                </Card>
              ) : (
                <Card className="p-12 rounded-2xl text-center">
                  <MailOpen className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-xl font-medium text-gray-600 mb-2">Select a notification</h3>
                  <p className="text-gray-500">Click on a notification to view its contents</p>
                </Card>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
